import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import { type ReactNode, useId } from 'react';
import type { ReaderMode } from '../contracts';

export interface InputBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'input' }>;
  readonly mode: ReaderMode;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * The Input block (§9.2): a label, one line or several, obligatory or not, and
 * a format that only means something on one line.
 *
 * ## The format is half of a double check
 *
 * « Vérifié deux fois : au navigateur pour la main de la personne, au serveur à
 * l'avancée de page, parce que le navigateur n'est pas une frontière de
 * confiance. » `type="email"` is the browser's half - it also gives a phone the
 * right keyboard - and `readerBlockers` greys the button on the same rule, so
 * the person is told before the press rather than refused after it.
 *
 * ## « · facultatif », and why the label says it
 *
 * `Lecteur.dc.html` writes it on the optional email of the consent page. An
 * obligatory field is the norm on these pages, so what is worth naming is the
 * one that is not - and a field which says nothing is a field somebody fills in
 * case. The editor's preview plate spells it « (facultatif) »; one spelling had
 * to win, and the reader's own plate is the one that decides what the reader
 * says.
 *
 * ## `readOnly` and never `disabled` outside `interactive`
 *
 * The repository's rule, from the criterion editor: a disabled control leaves
 * the tab order and its text stops being selectable, so a preview meant for
 * READING would be the one place a typed answer cannot be copied. In `edit` the
 * frame's target sits over the field anyway.
 */
export function InputBlock({ block, mode, value, onChange }: InputBlockProps): ReactNode {
  const id = useId();
  const readOnly = mode !== 'interactive';
  const label = block.required || block.label === '' ? block.label : `${block.label} · facultatif`;

  return (
    <div className="lecteur-champ">
      <label className="lecteur-etiquette" htmlFor={id}>
        {label}
      </label>
      {block.lines === 'multi' ? (
        <textarea
          id={id}
          className="lecteur-saisie"
          rows={4}
          required={block.required}
          readOnly={readOnly}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="lecteur-saisie"
          type={block.format === 'email' ? 'email' : 'text'}
          required={block.required}
          readOnly={readOnly}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
